import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import { useState, useEffect } from "react";
import { Project, ProjectDocument, SignatureRequest } from "types";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { projectsApi } from "@services/api/projects";
import { signatureRequestsApi } from "@services/api/signatureRequests";
import { SignatureStatusModal } from "@components/modals/SignatureStatusModal";
import { useDataStore } from "@store/user_org";

const getDocumentById = (
  project: Project,
  documentId: string,
): ProjectDocument | undefined => {
  for (const folder of project.folders) {
    const doc = folder.documents?.find((d) => d.id === documentId);
    if (doc) return doc;
    if (folder.sub_folders) {
      for (const sub of folder.sub_folders) {
        const subDoc = sub.documents?.find((d) => d.id === documentId);
        if (subDoc) return subDoc;
      }
    }
  }
  return undefined;
};

const updateDocumentInProject = (
  project: Project,
  documentId: string,
  updater: (doc: ProjectDocument) => ProjectDocument,
): Project => {
  return {
    ...project,
    folders: project.folders.map((folder) => ({
      ...folder,
      documents: folder.documents?.map((d) =>
        d.id === documentId ? updater(d) : d,
      ),
      sub_folders: folder.sub_folders?.map((sub) => ({
        ...sub,
        documents: sub.documents?.map((d) =>
          d.id === documentId ? updater(d) : d,
        ),
      })),
    })),
  };
};

const removeDocumentFromProject = (
  project: Project,
  documentId: string,
): Project => {
  return {
    ...project,
    folders: project.folders.map((folder) => ({
      ...folder,
      documents: folder.documents?.filter((d) => d.id !== documentId),
      sub_folders: folder.sub_folders?.map((sub) => ({
        ...sub,
        documents: sub.documents?.filter((d) => d.id !== documentId),
      })),
    })),
  };
};

interface DocumentModalProps {
  onClose: () => void;
  document_id: string;
  activeProject: Project;
  setActiveProject?: (project: Project) => void;
  readOnly?: boolean;
  hideSignatures?: boolean;
  onRequestSignature?: (doc: ProjectDocument) => void;
  signatureRequests?: SignatureRequest[];
}

export const DocumentModal = ({
  onClose,
  document_id,
  activeProject,
  setActiveProject,
  readOnly = false,
  hideSignatures = false,
  onRequestSignature,
  signatureRequests: externalSignatureRequests,
}: DocumentModalProps) => {
  const userData = useDataStore((s) => s.userData);
  const [document, setDocument] = useState<ProjectDocument | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [fetchedSignatureRequests, setFetchedSignatureRequests] = useState<SignatureRequest[]>([]);
  const [signatureStatusOpen, setSignatureStatusOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    // Always fetch fresh project to get valid signed URLs
    projectsApi.get(activeProject.id).then((freshProject) => {
      const freshDoc = getDocumentById(freshProject, document_id);
      if (freshDoc) {
        setDocument(freshDoc);
      } else {
        // Fallback to local data if not found in fresh response
        setDocument(getDocumentById(activeProject, document_id));
      }
    }).catch(() => {
      // Fallback to local data on network error
      setDocument(getDocumentById(activeProject, document_id));
    }).finally(() => {
      setLoading(false);
    });
  }, [document_id, activeProject.id]);

  // Fetch signature requests if not passed from parent
  useEffect(() => {
    if (externalSignatureRequests || !activeProject?.id) return;
    signatureRequestsApi.listForProject(activeProject.id)
      .then(setFetchedSignatureRequests)
      .catch(() => {});
  }, [activeProject?.id, externalSignatureRequests]);

  // Derive existing signature request from available data (no async matching).
  //
  // Priority order — both checks bind on STABLE keys, never on filename:
  //   1. `project_document_id` on the request — set at create time and
  //      never rewritten. Two docs sharing a name will never collide.
  //   2. fallback for legacy requests created before that field existed:
  //      match the doc's current file.id against the request's file ids.
  //      Once the doc's file.id is rewritten by prepared/signed flow, only
  //      `current_file.id` will still match.
  //
  // We INTENTIONALLY do not fall back to filename matching anywhere — that
  // was the source of the "signature landed on the wrong Application.pdf"
  // bug.
  const allSignatureRequests = externalSignatureRequests || fetchedSignatureRequests;
  const existingSignatureRequest = document
    ? allSignatureRequests.find((r) => {
        if (r.project_document_id && document.id && r.project_document_id === document.id) {
          return true;
        }
        const docFileId = document.file?.id || "";
        if (!docFileId) return false;
        return (
          r.original_file?.id === docFileId ||
          r.current_file?.id === docFileId
        );
      }) || null
    : null;

  // Signer button: shows for anyone (incl. guests) who is a pending signer on
  // this doc's signature request. Bypasses readOnly/hideSignatures so clients
  // can sign directly from the document viewer.
  const userEmail = (userData?.infos?.email || "").toLowerCase();
  const pendingSignerForMe = existingSignatureRequest && existingSignatureRequest.status !== "completed"
    ? existingSignatureRequest.signers.find(
        (s) =>
          s.status === "pending" &&
          !!s.sign_url &&
          (s.signer_email || "").toLowerCase() === userEmail,
      ) || null
    : null;



  function handleToggleHidden() {
    if (!document || !setActiveProject) return;

    const updatedDoc = { ...document, hidden: !document.hidden };
    setDocument(updatedDoc);
    setActiveProject(
      updateDocumentInProject(activeProject, document.id, () => updatedDoc),
    );
  }

  function handleDelete() {
    if (!document || !setActiveProject) return;

    setActiveProject(removeDocumentFromProject(activeProject, document.id));
    onClose();
  }

  // Prefer the signature request's current PDF (prepared / signed) over
  // the project document's original file. The project doc's file is never
  // mutated — the signed version lives only inside the request, so we
  // resolve it here when there's a match.
  const displayedPdfUrl =
    existingSignatureRequest?.current_file?.url ||
    document?.file?.url ||
    "";

  function handleOpenExternal() {
    if (displayedPdfUrl) {
      window.open(displayedPdfUrl, "_blank");
    }
  }

  if (!document && !loading) {
    return null;
  }

  return (
    <BaseModal
      width="max-w-800"
      title={document?.name || "Document"}
      startIcon={<Icon1 name="file-text" size={20} />}
      onClose={onClose}
    >
      {confirmationOpen && (
        <ConfirmationModal
          onClose={() => setConfirmationOpen(false)}
          onConfirm={handleDelete}
          title="Supprimer le document"
          message="Êtes-vous sûr de vouloir supprimer ce document ?"
          confirmText="CONFIRMER"
        />
      )}
      {signatureStatusOpen && existingSignatureRequest && (
        <SignatureStatusModal
          onClose={() => setSignatureStatusOpen(false)}
          signatureRequest={existingSignatureRequest}
        />
      )}

      <div className="bg-white flex flex-col gap-4 p-4">
        {/* Document Display */}
        <div
          className="relative w-full rounded-xl overflow-hidden border"
          style={{ height: "500px" }}
        >
          {loading || !document ? (
            <div className="w-full h-full flex items-center justify-center bg-neutral-50">
              <span className="text-sm text-neutral-400">Chargement...</span>
            </div>
          ) : (
            <>
              <iframe
                src={displayedPdfUrl}
                title={document.name}
                className="w-full h-full"
                style={{ border: "none" }}
              />
              {document.hidden && (
                <div className="absolute inset-0 bg-black/30 rounded-xl flex items-center justify-center pointer-events-none">
                  <Icon1 name="invisible" size={32} color="white" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Controls */}
        {document && (
          <div className="flex flex-row justify-center gap-2.5 items-center">
            <button
              onClick={handleOpenExternal}
              className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
              title="Ouvrir dans un nouvel onglet"
            >
              <Icon1 name="open" size={20} color="white" />
            </button>

            {!readOnly && (
              <button
                onClick={handleToggleHidden}
                className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
                title={document.hidden ? "Rendre visible" : "Masquer le document"}
              >
                <Icon1 name="invisible" size={24} color="white" />
              </button>
            )}

            {!readOnly && !hideSignatures && document.file?.id && existingSignatureRequest && existingSignatureRequest.status !== "completed" && (
              <button
                onClick={() => setSignatureStatusOpen(true)}
                className="h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center gap-2 px-4"
                title="Suivi de signature"
              >
                <Icon1 name="sign" size={24} color="white" />
                <span className="text-white text-h4 font-medium">Signatures</span>
              </button>
            )}

            {pendingSignerForMe && pendingSignerForMe.sign_url && (
              <button
                onClick={() => window.open(pendingSignerForMe.sign_url!, "_blank")}
                className="h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 px-4"
                title="Signer le document"
              >
                <Icon1 name="sign" size={24} color="white" />
                <span className="text-white text-h4 font-medium">Signer</span>
              </button>
            )}

            {!readOnly && !hideSignatures && document.file?.id && !existingSignatureRequest
              && !document.file.id.startsWith("generated_documents/")
              && !document.file.id.startsWith("signature_requests/")
              && onRequestSignature && (
              <button
                onClick={() => {
                  onRequestSignature(document);
                  onClose();
                }}
                className="h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center gap-2 px-4"
                title="Demander une signature"
              >
                <Icon1 name="sign" size={24} color="white" />
                <span className="text-white text-h4 font-medium">Signer</span>
              </button>
            )}

            {!readOnly && (
              <button
                onClick={() => setConfirmationOpen(true)}
                className="w-12 h-12 rounded-full bg-neutral-800 hover:bg-neutral-700 transition-colors flex items-center justify-center"
                title="Supprimer le document"
              >
                <Icon1 name="delete" size={24} color="white" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <Button variant="small" label="Fermer" onClick={onClose} />
      </div>
    </BaseModal>
  );
};
